import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, Users, Percent, Plus, Trash2, Loader2, Check, Music, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import SEO from "../components/SEO";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RoyaltiesPage() {
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16 px-4" data-testid="royalties-page">
      <SEO title="Royalty Splits" description="Manage royalty splits for your collaborative beats." />
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Badge className="bg-neon-green/20 text-neon-green border-0 mb-3">Earnings</Badge>
          <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight">
            Royalty <span className="text-neon-cyan">Splits</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Manage splits and track earnings from collaborative beats.</p>
        </div>

        <Tabs defaultValue="earnings" className="w-full">
          <TabsList className="w-full bg-zinc-900 border border-white/10 mb-8 h-11">
            <TabsTrigger value="earnings" className="flex-1 data-[state=active]:bg-neon-cyan data-[state=active]:text-black" data-testid="tab-earnings">
              <DollarSign className="w-4 h-4 mr-2" /> My Earnings
            </TabsTrigger>
            <TabsTrigger value="configure" className="flex-1 data-[state=active]:bg-neon-cyan data-[state=active]:text-black" data-testid="tab-configure">
              <Percent className="w-4 h-4 mr-2" /> Configure Splits
            </TabsTrigger>
          </TabsList>

          <TabsContent value="earnings">
            <EarningsTab token={token} headers={headers} />
          </TabsContent>
          <TabsContent value="configure">
            <ConfigureTab token={token} headers={headers} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function EarningsTab({ token, headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/api/royalties/my-earnings`, { headers })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return <p className="text-zinc-500 text-center py-12">Sign in to view earnings</p>;
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-neon-cyan" /></div>;
  if (!data) return <p className="text-zinc-500 text-center py-12">No earnings data available</p>;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Check className="w-4 h-4 text-neon-green" />
            <span className="text-xs text-zinc-500 uppercase">Confirmed Earnings</span>
          </div>
          <p className="text-2xl font-bold text-neon-green" data-testid="confirmed-earnings">${data.total_earned}</p>
        </div>
        <div className="glass border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-neon-yellow" />
            <span className="text-xs text-zinc-500 uppercase">Pending</span>
          </div>
          <p className="text-2xl font-bold text-neon-yellow" data-testid="pending-earnings">${data.total_pending}</p>
        </div>
      </div>

      {/* Transactions */}
      <div className="glass border border-white/10 rounded-xl p-5">
        <h3 className="text-xs text-zinc-400 uppercase tracking-wider mb-4">Transaction History</h3>
        {data.transactions.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-8">No royalty transactions yet</p>
        ) : (
          <div className="space-y-2">
            {data.transactions.map(t => (
              <div key={t.split_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/40">
                <div>
                  <p className="text-white text-sm">Beat: {t.beat_id}</p>
                  <p className="text-zinc-500 text-xs">{t.percentage}% split</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${t.status === "confirmed" ? "text-neon-green" : "text-neon-yellow"}`}>${t.amount}</p>
                  <Badge className={`${t.status === "confirmed" ? "bg-neon-green/20 text-neon-green" : "bg-neon-yellow/20 text-neon-yellow"} border-0 text-[10px]`}>
                    {t.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigureTab({ token, headers }) {
  const [beats, setBeats] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState("");
  const [config, setConfig] = useState(null);
  const [producers, setProducers] = useState([]);
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    Promise.all([
      axios.get(`${API}/api/beats`, { headers }),
      axios.get(`${API}/api/producers/directory`)
    ]).then(([beatsRes, prodsRes]) => {
      setBeats(beatsRes.data);
      setProducers(prodsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!selectedBeat) { setConfig(null); setSplits([]); return; }
    axios.get(`${API}/api/royalties/config/${selectedBeat}`)
      .then(res => {
        setConfig(res.data);
        setSplits(res.data.splits || []);
      }).catch(() => {});
  }, [selectedBeat]);

  const addSplit = () => {
    setSplits(prev => [...prev, { user_id: "", percentage: 0 }]);
  };

  const removeSplit = (idx) => {
    setSplits(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSplit = (idx, field, value) => {
    setSplits(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const totalPct = splits.reduce((a, s) => a + (parseFloat(s.percentage) || 0), 0);
  const ownerPct = 100 - totalPct;

  const saveSplits = async () => {
    if (!selectedBeat) return;
    if (totalPct > 100) { toast.error("Total splits exceed 100%"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/royalties/configure`, {
        beat_id: selectedBeat,
        splits: splits.filter(s => s.user_id && s.percentage > 0)
      }, { headers });
      setConfig(res.data);
      toast.success("Splits saved!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (!token) return <p className="text-zinc-500 text-center py-12">Sign in to configure splits</p>;
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-neon-cyan" /></div>;

  return (
    <div className="space-y-6">
      {/* Beat Selector */}
      <div className="glass border border-white/10 rounded-xl p-5">
        <h3 className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Select Beat</h3>
        <Select value={selectedBeat} onValueChange={setSelectedBeat}>
          <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="royalty-beat-select">
            <SelectValue placeholder="Choose a beat to configure splits..." />
          </SelectTrigger>
          <SelectContent>
            {beats.map(b => <SelectItem key={b.beat_id} value={b.beat_id}>{b.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selectedBeat && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="glass border border-white/10 rounded-xl p-5 space-y-4"
        >
          <div className="flex justify-between items-center">
            <h3 className="text-xs text-zinc-400 uppercase tracking-wider">Revenue Split</h3>
            <Button variant="outline" size="sm" onClick={addSplit}
              className="border-neon-cyan text-neon-cyan text-xs" data-testid="add-split-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Collaborator
            </Button>
          </div>

          {/* Owner's share */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-neon-cyan/5 border border-neon-cyan/20">
            <div className="w-8 h-8 rounded-full bg-neon-cyan/20 flex items-center justify-center">
              <Music className="w-4 h-4 text-neon-cyan" />
            </div>
            <span className="text-white text-sm font-medium flex-1">You (Owner)</span>
            <span className={`font-bold text-lg ${ownerPct < 0 ? "text-red-400" : "text-neon-cyan"}`} data-testid="owner-percentage">
              {ownerPct}%
            </span>
          </div>

          {/* Collaborator splits */}
          <AnimatePresence>
            {splits.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/40"
              >
                <div className="w-8 h-8 rounded-full bg-neon-magenta/20 flex items-center justify-center">
                  <Users className="w-4 h-4 text-neon-magenta" />
                </div>
                <div className="flex-1">
                  <Select value={s.user_id} onValueChange={v => updateSplit(i, "user_id", v)}>
                    <SelectTrigger className="bg-zinc-800 border-white/10 h-8 text-xs" data-testid={`split-user-${i}`}>
                      <SelectValue placeholder="Select producer" />
                    </SelectTrigger>
                    <SelectContent>
                      {producers.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number" value={s.percentage}
                    onChange={e => updateSplit(i, "percentage", parseFloat(e.target.value) || 0)}
                    className="w-16 h-8 bg-zinc-800 border-white/10 text-center text-xs"
                    data-testid={`split-pct-${i}`}
                  />
                  <span className="text-zinc-400 text-xs">%</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeSplit(i)} className="text-zinc-500 hover:text-red-400 w-8 h-8">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Visual split bar */}
          {splits.length > 0 && (
            <div className="h-3 rounded-full overflow-hidden flex bg-zinc-800">
              <div className="bg-neon-cyan h-full transition-all" style={{ width: `${Math.max(0, ownerPct)}%` }} />
              {splits.map((s, i) => (
                <div key={i} className="bg-neon-magenta h-full transition-all" style={{ width: `${s.percentage || 0}%`, opacity: 0.6 + (i * 0.1) }} />
              ))}
            </div>
          )}

          <Button onClick={saveSplits} disabled={saving || ownerPct < 0}
            className="w-full bg-neon-cyan text-black font-bold" data-testid="save-splits-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save Splits
          </Button>
        </motion.div>
      )}
    </div>
  );
}
